import { ReactNode } from 'react';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';

interface TabCountBadgeProps {
  label: ReactNode;
  count?: number;
  /**
   * Render the count as a pill after the label instead of a badge floating
   * over the tab's corner. Sub-tabs are short pills inside a padded, clipping
   * scroller, which would cut an overhanging badge off.
   */
  inline?: boolean;
}

const formatCount = (count: number) => (count > 999 ? '999+' : String(count));

/**
 * Wraps a tab title with the number of items that tab's list will show, e.g.
 * "Contracts Ⓝ". Renders the plain label until the count has loaded
 * (`count === undefined`, including permission-denied/error), and always shows
 * it afterwards - including 0 - so it reads as "how many instances are on this
 * page" rather than as an unread-style alert.
 */
export const TabCountBadge = ({ label, count, inline = false }: TabCountBadgeProps) => {
  if (count === undefined) {
    return <>{label}</>;
  }

  if (inline) {
    return (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
        {label}
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 20,
            height: 18,
            px: 0.625,
            borderRadius: 9,
            background: 'var(--colorfulBg)',
            color: '#fff',
            fontSize: '0.6875rem',
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          {formatCount(count)}
        </Box>
      </Box>
    );
  }

  return (
    <Badge
      badgeContent={count}
      max={999}
      showZero
      sx={{
        '& .MuiBadge-badge': {
          background: 'var(--colorfulBg)',
          color: '#fff',
        },
      }}
    >
      <Box component="span" sx={{ pr: 1 }}>
        {label}
      </Box>
    </Badge>
  );
};
